import type { Lead, ScoringResult, Answer } from '@/types';
import type { ProductConfig } from '@/types/product';

export type FinalizePayload = {
  sessionId: string;
  productId: string;
  leadId?: number | null;
  funilId?: number | null;
  origem?: string | null;
  lead: Pick<Lead, 'nome' | 'email' | 'telefone' | 'cidade' | 'campanha'> & {
    origem?: string;
    honeypot?: string;
  };
  scoring: Pick<ScoringResult, 'totalScore' | 'totalPercentage' | 'finalRecommendation' | 'categoryScores'>;
  product: Pick<ProductConfig, 'id' | 'title' | 'funilId' | 'crmOrigem'>;
  answers?: Answer[];
  pdfBase64: string;
};

function getApiUrl(): string {
  const raw = (import.meta.env.VITE_RELIABLE_FINALIZE_URL as string | undefined) || '/api/diagnostic/finalize/';
  return raw.endsWith('/') ? raw : raw + '/';
}

export function reliableDeliveryEnabled(): boolean {
  const raw = String(import.meta.env.VITE_RELIABLE_DELIVERY ?? 'true');
  return raw === 'true';
}

async function blobToBase64(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('file_reader_error'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(blob);
  });
}

export async function enqueueFinalize(payload: Omit<FinalizePayload, 'pdfBase64'> & { pdfBlob: Blob }): Promise<{ ok: boolean; jobId?: number }> {
  const meta: Omit<FinalizePayload, 'pdfBase64'> = {
    sessionId: payload.sessionId,
    productId: payload.productId,
    leadId: payload.leadId ?? null,
    funilId: payload.funilId ?? null,
    origem: payload.origem ?? null,
    lead: payload.lead,
    scoring: payload.scoring,
    product: payload.product,
    answers: payload.answers
  };

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(new Error('timeout')), 45000);

  try {
    const apiUrl = getApiUrl();
    const fd = new FormData();
    fd.append('payload', JSON.stringify(meta));
    fd.append('pdf', payload.pdfBlob, `diagnostico_${payload.sessionId}.pdf`);

    const res = await fetch(apiUrl, {
      method: 'POST',
      body: fd,
      signal: controller.signal
    });

    if (!res.ok) {
      return { ok: false };
    }
    const json = await res.json().catch(() => null) as { ok?: boolean; jobId?: number } | null;
    if (json && json.ok) return { ok: true, jobId: json.jobId };
    return { ok: true };
  } catch {
    try {
      const pdfBase64 = await blobToBase64(payload.pdfBlob);
      const body: FinalizePayload = {
        ...(meta as Omit<FinalizePayload, 'pdfBase64'>),
        pdfBase64
      };
      const res = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) return { ok: false };
      const json = await res.json().catch(() => null) as { ok?: boolean; jobId?: number } | null;
      if (json && json.ok) return { ok: true, jobId: json.jobId };
      return { ok: true };
    } catch {
      return { ok: false };
    }
  } finally {
    clearTimeout(t);
  }
}

